/* Just Brilliant: coordinated cart requests shared by the drawer and cart page. */
(() => {
  if (window.JBCart) return;
  let revision = 0;
  let uncertain = false;
  let mutations = 0;
  let queue = Promise.resolve();
  const sections = () => [
    { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
    { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
    { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
  ].filter(section => document.getElementById(section.id));
  const emit = (name, detail) => document.dispatchEvent(new CustomEvent(name, { detail }));
  const endpoint = path => path.replace(/\.js$/, '') + '.js';
  function error(view, message) {
    let status = view.querySelector('[data-jb-cart-error]');
    if (!status) {
      status = document.createElement('p');
      status.dataset.jbCartError = '';
      status.className = 'form-notification error';
      status.setAttribute('role', 'alert');
      (view.querySelector('.side-panel-content') || view).prepend(status);
    }
    status.textContent = typeof message === 'string' ? message : 'Your bag could not be updated. Please try again.';
    status.hidden = false;
  }
  function render(htmlBySection, requested) {
    if (!htmlBySection || typeof htmlBySection !== 'object') return false;
    let complete = true;
    const active = document.activeElement;
    const activeId = active?.id;
    const wasQuantity = active?.matches('input.qty');
    requested.forEach(section => {
      const host = document.getElementById(section.id);
      const html = htmlBySection[section.section];
      if (!host) return;
      if (typeof html !== 'string') { complete = false; return; }
      const replacement = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      if (!replacement) { complete = false; return; }
      const target = host.querySelector(section.selector) || host;
      target.innerHTML = replacement.innerHTML;
    });
    if (wasQuantity && activeId) document.getElementById(activeId)?.focus({ preventScroll: true });
    emit('cart:rendered', {});
    return complete;
  }
  async function request(url, options = {}) {
    const writing = options.method && options.method.toUpperCase() !== 'GET';
    if (writing && uncertain) {
      const failure = new Error('Please refresh the page to confirm your bag before making another change.');
      failure.name = 'CartTimeoutError';
      throw failure;
    }
    const controller = new AbortController();
    const abort = () => controller.abort(options.signal?.reason);
    if (options.signal?.aborted) abort();
    else options.signal?.addEventListener('abort', abort, { once: true });
    let expired = false;
    const timeout = setTimeout(() => { expired = true; controller.abort(); }, 20000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      const state = await response.json();
      return { ok: response.ok, json: async () => state };
    } catch (failure) {
      // A lost connection or unreadable response can follow a completed POST.
      // Never invite a duplicate mutation when the server outcome is unknown.
      if (writing) uncertain = true;
      if (expired) {
        const error = new Error('The request timed out. Your bag may have changed. Refresh the page to check it before trying again.');
        error.name = 'CartTimeoutError';
        throw error;
      }
      if (writing) {
        const error = new Error('We could not confirm whether your bag changed. Refresh the page to check it before trying again.');
        error.name = 'CartUncertainError';
        throw error;
      }
      throw failure;
    } finally { clearTimeout(timeout); options.signal?.removeEventListener('abort', abort); }
  }
  async function parse(response) {
    const state = await response.json();
    if (!state || typeof state !== 'object') throw new Error('Your bag could not be loaded. Please refresh the page.');
    if (!response.ok || state.errors || state.status >= 400) {
      throw new Error(typeof state.errors === 'string' ? state.errors : state.description || state.message || 'Your bag could not be updated. Please try again.');
    }
    return state;
  }
  function singlePieceError(message) {
    const failure = new Error(message || theme.singlePiece.checkFailed);
    failure.name = 'CartQuantityError';
    return failure;
  }
  async function singlePieceState() {
    const url = new URL(theme.routes.cart_url, location.origin);
    url.searchParams.set('view', 'jb-quantity');
    try {
      const state = await parse(await request(url, { credentials: 'same-origin', cache: 'no-store' }));
      if (!Array.isArray(state.items) || state.items.some(item => typeof item.key !== 'string' || typeof item.limited !== 'boolean' || !item.product_id || !Number.isInteger(item.quantity))) throw singlePieceError();
      return state;
    } catch (failure) {
      if (/^Cart/.test(failure.name)) throw failure;
      throw singlePieceError();
    }
  }
  async function enforceSinglePiece() {
    const state = await singlePieceState();
    const seen = new Set();
    const updates = {};
    state.items.forEach(item => {
      if (!item.limited) return;
      const id = String(item.product_id);
      const quantity = seen.has(id) ? 0 : 1;
      seen.add(id);
      if (item.quantity !== quantity) updates[item.key] = quantity;
    });
    if (!Object.keys(updates).length) return state;
    const requested = sections();
    const changed = await parse(await request(endpoint(theme.routes.cart_update_url), {
      method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates, sections: requested.map(item => item.section), sections_url: location.pathname + location.search })
    }));
    render(changed.sections, requested);
    emit('line-item:change:end', { cart: changed });
    const verified = await singlePieceState();
    const products = new Set();
    for (const item of verified.items) {
      if (!item.limited) continue;
      const id = String(item.product_id);
      if (item.quantity !== 1 || products.has(id)) throw singlePieceError();
      products.add(id);
    }
    const view = document.getElementById('Cart') || document.getElementById('Cart-Drawer');
    if (view) error(view, theme.singlePiece.adjusted);
    return verified;
  }
  async function add(url, options, policy = {}) {
    if (!policy.limited) return request(url, options);
    if (!policy.productId || !(options.body instanceof FormData)) throw singlePieceError();
    const state = await enforceSinglePiece();
    if (state.items.some(item => item.limited && String(item.product_id) === String(policy.productId))) throw singlePieceError(theme.singlePiece.message);
    options.body.set('quantity', '1');
    return request(url, options);
  }
  function mutate(view, line, quantity, clear) {
    if (!clear && (!Number.isInteger(Number(line)) || Number(line) < 1)) return Promise.resolve(false);
    if (!clear && (String(quantity).trim() === '' || !Number.isInteger(Number(quantity)) || Number(quantity) < 0)) {
      error(view, 'Please enter a valid quantity.');
      return Promise.resolve(false);
    }
    const row = view.querySelector(`#CartItem-${line}, #CartDrawerItem-${line}`);
    if (row?.dataset.jbSinglePiece === 'true' && Number(quantity) > 1) quantity = 1;
    const key = row?.dataset.cartKey || row?.dataset.productId || row?.querySelector('quantity-selector')?.dataset.id;
    if (!clear && !key && !Number.isInteger(Number(line))) return Promise.resolve(false);
    const pendingKey = clear ? 'clear' : `${key || line}:${Number(quantity)}`;
    view._jbCartRequests ||= new Map();
    const previous = view._jbCartLastRequest;
    if (previous?.key === pendingKey && view._jbCartRequests.has(previous.token)) return previous.promise;
    const changeVersion = ++revision;
    mutations++;
    view._jbCartRefreshController?.abort();
    view.classList.add('cart-disabled');
    view.setAttribute('aria-busy', 'true');
    row?.classList.add('loading', 'thb-loading');
    view.querySelectorAll('[data-jb-cart-error]').forEach(node => node.hidden = true);
    emit('line-item:change:start', { quantity });
    const run = async () => {
      if (!view.isConnected) return false;
      const requested = sections();
      const body = { sections: requested.map(item => item.section), sections_url: location.pathname + location.search };
      if (!clear) Object.assign(body, key ? { id: key, quantity: Number(quantity) } : { line: Number(line), quantity: Number(quantity) });
      try {
        const state = await parse(await request(endpoint(clear ? theme.routes.cart_clear_url : theme.routes.cart_change_url), {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body)
        }));
        if (!render(state.sections, requested)) {
          // Shopify may apply a change even if an included section cannot render.
          // Recover the HTML with a read request; never repeat the cart mutation.
          try {
            const url = new URL(location.href);
            url.searchParams.set('sections', requested.map(item => item.section).join(','));
            const html = await parse(await request(url, { credentials: 'same-origin' }));
            if (changeVersion === revision && !render(html, requested)) error(view, 'Your bag was updated. Refresh the page to see the latest contents.');
          } catch (_) { error(view, 'Your bag was updated. Refresh the page to see the latest contents.'); }
        }
        emit('line-item:change:end', { quantity, cart: state });
        return true;
      } catch (failure) {
        if (view.isConnected) {
          error(view, failure.message);
          row?.querySelectorAll('input.qty').forEach(input => input.value = input.defaultValue);
        }
        emit('cart:error', { message: failure.message });
        return false;
      }
    };
    const pending = queue.then(run, run).finally(() => {
      view._jbCartRequests.delete(changeVersion);
      if (!view._jbCartRequests.size) {
        view.classList.remove('cart-disabled');
        view.removeAttribute('aria-busy');
        view._jbCartPending = null;
      }
      row?.classList.remove('loading', 'thb-loading');
      mutations--;
    });
    view._jbCartRequests.set(changeVersion, pending);
    view._jbCartLastRequest = { key: pendingKey, token: changeVersion, promise: pending };
    view._jbCartPending = pending;
    queue = pending;
    return pending;
  }
  async function refresh(view) {
    if (mutations) return queue;
    view._jbCartRefreshController?.abort();
    const controller = new AbortController();
    view._jbCartRefreshController = controller;
    const version = revision;
    const requested = sections();
    const url = new URL(location.href);
    url.searchParams.set('sections', requested.map(item => item.section).join(','));
    try {
      const state = await parse(await request(url, { signal: controller.signal, credentials: 'same-origin' }));
      if (version === revision && !controller.signal.aborted && view.isConnected) render(state, requested);
    } catch (_) { /* A failed background refresh keeps the rendered bag usable. */ }
    finally { if (view._jbCartRefreshController === controller) view._jbCartRefreshController = null; }
  }
  function saveNote(view, value) {
    const run = async () => {
    try {
      await parse(await request(endpoint(theme.routes.cart_update_url), {
        method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note: value })
      }));
      return true;
    } catch (failure) { if (view.isConnected) error(view, /^Cart/.test(failure.name) ? failure.message : 'Your order note could not be saved. Please try again.'); return false; }
    };
    const pending = queue.then(run, run);
    queue = pending;
    return pending;
  }
  function enqueue(task) {
    revision++; mutations++;
    const pending = queue.then(task, task).finally(() => { mutations--; });
    queue = pending;
    return pending;
  }
  document.addEventListener('cart:item-added', () => { revision++; });
  window.JBCart = { request, add, enqueue, enforceSinglePiece, render, sections, update: (view, line, quantity) => mutate(view, line, quantity, false), clear: view => mutate(view, null, 0, true), refresh, saveNote, error };
  if (!theme.singlePiece.builderPage) {
    const check = () => {
      if (!document.querySelector('[data-cart-key][data-jb-single-piece="true"], [data-product-id][data-jb-single-piece="true"]')) return;
      enqueue(enforceSinglePiece).catch(failure => {
        const view = document.getElementById('Cart') || document.getElementById('Cart-Drawer');
        if (view) error(view, failure.message);
      });
    };
    window.addEventListener('pageshow', check);
    let checkingCheckout = false;
    document.addEventListener('submit', event => {
      const form = event.target;
      const view = form.closest('theme-cart, cart-drawer');
      if (event.defaultPrevented || !view || event.submitter?.name !== 'checkout') return;
      if (!view.querySelector('[data-jb-single-piece="true"]')) return;
      event.preventDefault();
      if (checkingCheckout) return;
      checkingCheckout = true;
      enqueue(async () => {
        await enforceSinglePiece();
        const note = form.querySelector('[name="note"]');
        if (note) await parse(await request(endpoint(theme.routes.cart_update_url), {
          method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note: note.value })
        }));
        location.assign(new URL(theme.routes.root_url + 'checkout', location.origin).href);
      }).catch(failure => error(view, failure.message)).finally(() => { checkingCheckout = false; });
    });
  }
})();
